import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { SessionExpiryNoticeService } from '../../../services/session-expiry-notice.service';

/**
 * "Your session has ended, log in again" notice (VE-SIGNALR). Placed once in the app shell; visible while
 * {@link SessionExpiryNoticeService.expired} is true. Sign in goes through the existing legacy-login handoff, so the
 * admin comes back to the same page. It hides itself when a fresh token appears (e.g. a sign-in in another tab).
 * Fixed at the top so it is seen on every layout, including pages that hide the V2 header.
 */
@Component({
  selector: 'app-session-expired-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (notice.expired()) {
      <div class="session-banner" role="alert" aria-live="assertive" data-testid="session-expired-banner">
        <span class="session-banner__icon" aria-hidden="true">i</span>
        <div class="session-banner__body">
          <p class="session-banner__text" data-testid="session-expired-text">
            Your session has ended. Please log in again to continue.
          </p>
          <p class="session-banner__hint" data-testid="session-expired-hint">
            This is not an error. For security, you are signed out automatically after a few hours. After you log in
            you come back to this page.
          </p>
        </div>
        <button
          type="button"
          class="session-banner__btn"
          data-testid="session-expired-sign-in"
          (click)="notice.signIn()"
        >
          Log in again
        </button>
      </div>
    }
  `,
  styles: [
    `
      :host {
        position: fixed;
        top: 12px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 10000;
        width: min(640px, calc(100vw - 32px));
        pointer-events: none;
      }
      .session-banner {
        pointer-events: auto;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px 12px;
        box-sizing: border-box;
        width: 100%;
        padding: 10px 12px;
        background: #fffbeb;
        border: 1px solid #fcd34d;
        border-left: 6px solid #d97706;
        border-radius: var(--radius-md, 8px);
        color: #78350f;
        font-family: var(--font-family, inherit);
        box-shadow: 0 4px 14px rgba(180, 83, 9, 0.22);
      }
      .session-banner__icon {
        flex: 0 0 auto;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
        font-weight: 800;
        color: #fff;
        background: #d97706;
      }
      .session-banner__body {
        flex: 1 1 220px;
        min-width: 0;
      }
      .session-banner__text {
        margin: 0;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.45;
      }
      .session-banner__hint {
        margin: 3px 0 0;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.45;
      }
      .session-banner__btn {
        flex: 0 0 auto;
        font: inherit;
        font-size: 12.5px;
        font-weight: 700;
        padding: 8px 14px;
        border-radius: var(--radius-sm, 6px);
        border: 1px solid #b45309;
        background: #b45309;
        color: #fff;
        cursor: pointer;
      }
      .session-banner__btn:hover {
        filter: brightness(0.92);
      }
      .session-banner__btn:focus-visible {
        outline: 2px solid #2563eb;
        outline-offset: 2px;
      }
      @media (max-width: 480px) {
        .session-banner__btn {
          width: 100%;
        }
      }
    `,
  ],
})
export class SessionExpiredBannerComponent {
  protected readonly notice = inject(SessionExpiryNoticeService);
}
